import { $previewDataUri } from '#components/product-customization-board'
import { Canvg } from 'canvg'
import { capitalCase, snakeCase } from 'change-case'
import { get, isString, pickBy } from 'lodash'
import queryString from 'query-string'
import {
  $initialLayerColors,
  $inputLayersUrl,
  $isPrintSimulationLoading,
  $isPrintSimulationMatchPreview,
  $outputLayers,
  $previewResolutionWidth,
  $previewWatermarkImageUrl,
  $printSimulationAbortController,
  $printSimulationDataUri,
  $printSimulationError,
  $virtualLayersEl
} from './product-customization-board.store'

export type Layer = {
  id: string
  fieldId: string
  fieldName: string
  fieldLabel: string
  color: string
}

export const getLayerColorsFromQueryParams = () => {
  const queryParams = queryString.parse(location.search)
  const layerColors = pickBy(queryParams, (value) => isString(value) && value.startsWith('#')) as Record<string, string>

  return layerColors
}

export const createVirtualLayersEl = async () => {
  const inputLayersUrl = $inputLayersUrl.get()
  const watermarkImageUrl = $previewWatermarkImageUrl.get()

  if (!inputLayersUrl || !watermarkImageUrl) return

  const inputLayers = await fetchAndNormalizeSvgImage(inputLayersUrl, '3600px', '1800px')
  const watermark = await fetchAndNormalizeSvgImage(watermarkImageUrl, '100px', '100px')

  // Adjust watermark attributes
  const watermarkScaleWidth = parseFloat(inputLayers.width) / parseFloat(watermark.width)
  const watermarkScaleHeight = parseFloat(inputLayers.height) / parseFloat(watermark.height)
  const watermarkScale = Math.max(watermarkScaleWidth, watermarkScaleHeight)
  const watermarkFirstElement = watermark.svgElement.firstElementChild as HTMLElement

  watermarkFirstElement.setAttribute('opacity', '0.15')
  watermarkFirstElement.setAttribute('transform', `translate(0, 60) scale(${watermarkScale})`)

  // Append watermark to SVG element
  inputLayers.svgElement.appendChild(watermarkFirstElement)

  // Save SVG element to store
  $virtualLayersEl.set(inputLayers.svgElement)
}

export const processLayersData = (targetLayerColors?: Record<string, string>) => {
  const layerElement = $virtualLayersEl.get()

  if (!layerElement) {
    $outputLayers.set([])

    return
  }

  const layers: Layer[] = []
  const groupEls = layerElement.querySelectorAll<HTMLElement>('g[id]')
  const queryParams = queryString.parse(location.search)

  groupEls.forEach((groupEl) => {
    const pathEls = [...groupEl.children].filter((child) => child.tagName === 'path') as SVGPathElement[]

    // Just ignore group without direct paths, because it's not core layer
    if (!pathEls.length) return

    const id = groupEl.id
    const casedGroupId = snakeCase(groupEl.id)

    if (id !== casedGroupId) {
      // eslint-disable-next-line no-console
      console.warn(`Layer ID "${id}" is not snake case. Please use "${casedGroupId}" instead. Skip!`)

      return
    }

    const initialLayerColors = $initialLayerColors.get()
    const initialColor = initialLayerColors[id]

    const targetLayerColor = targetLayerColors?.[id]

    const queryParamColor = queryParams[id] as string | undefined

    const groupAttributeColor = groupEl.getAttribute('stroke') ?? groupEl.getAttribute('fill')

    const firstPathElWithColor = [...pathEls].find(
      (pathEl) => pathEl.hasAttribute('stroke') || pathEl.hasAttribute('fill')
    )
    const firstPathAttributeColor =
      firstPathElWithColor?.getAttribute('stroke') ?? firstPathElWithColor?.getAttribute('fill')

    const fallbackColor = id === 'background' ? '#ffffff' : '#000000'

    const color =
      targetLayerColor ??
      queryParamColor ??
      initialColor ??
      groupAttributeColor ??
      firstPathAttributeColor ??
      fallbackColor

    // Store color into initial layer colors
    // for the first time the layer is processed (if not already stored)
    if (!initialColor) {
      initialLayerColors[id] = color
      $initialLayerColors.set(initialLayerColors)
    }

    layers.push({
      id,
      fieldId: `_customization_color_${id}`,
      fieldName: capitalCase(id),
      fieldLabel: capitalCase(id),
      color
    })
  })

  $outputLayers.set(layers)
}

export const processLayersVisualization = () => {
  const layerElement = $virtualLayersEl.get()

  if (!layerElement) return

  const layers = $outputLayers.get()

  layers.forEach((layer) => {
    const groupEl = layerElement.querySelector(`g[id="${layer.id}"]`) as HTMLElement

    if (!groupEl) return

    // First, apply color to group element if available
    if (groupEl.hasAttribute('fill')) groupEl.setAttribute('fill', layer.color)
    if (groupEl.hasAttribute('stroke')) groupEl.setAttribute('stroke', layer.color)

    // Then, apply color to all nested path elements if available
    const pathEls = groupEl.querySelectorAll<SVGPathElement>('path')

    pathEls.forEach((pathEl) => {
      if (pathEl.hasAttribute('fill')) pathEl.setAttribute('fill', layer.color)
      if (pathEl.hasAttribute('stroke')) pathEl.setAttribute('stroke', layer.color)
    })
  })
}

export const updateLayersQueryParams = () => {
  const layers = $outputLayers.get()
  const queryParams = queryString.parse(location.search)

  layers.forEach((layer) => {
    queryParams[layer.id] = layer.color
  })

  const newQueryParams = queryString.stringify(queryParams)

  history.replaceState(null, '', `${location.pathname}?${newQueryParams}`)
}

export const renderLivePreview = async () => {
  const layerElement = $virtualLayersEl.get()

  if (!layerElement) return

  try {
    const markup = new XMLSerializer().serializeToString(layerElement)
    const dataUri = `data:image/svg+xml;base64,${btoa(markup)}`

    $previewDataUri.set(dataUri)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to render preview:', error)
  }
}

export const renderPreviewFile = async () => {
  const layerElement = $virtualLayersEl.get()

  if (!layerElement) return

  try {
    const { width, height } = getPreviewResolution() ?? {}

    if (!width || !height) return

    const clonedLayerElement = layerElement.cloneNode(true) as HTMLElement

    clonedLayerElement.setAttribute('width', `${width}px`)
    clonedLayerElement.setAttribute('height', `${height}px`)

    const currentUnixTime = new Date().getTime()
    const fileName = `customization_preview_${currentUnixTime}.png`

    return await renderPngFromSvgElement(clonedLayerElement, fileName)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to render preview file:`, error)

    return
  }
}

export const renderPrintOutputSimulation = async () => {
  $printSimulationDataUri.set('')
  $printSimulationError.set('')
  $isPrintSimulationLoading.set(true)
  $isPrintSimulationMatchPreview.set(false)

  const prevAbortController = $printSimulationAbortController.get()

  if (prevAbortController) {
    prevAbortController.abort()
  }

  const abortController = new AbortController()

  $printSimulationAbortController.set(abortController)

  const endpoint = `${window.__GLOBAL__.customApiBaseUrl}/image-customization/simulate-print-output`
  const previewFile = await renderPreviewFile()

  if (!previewFile) return

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': previewFile.type,
        'ngrok-skip-browser-warning': 'true'
      },
      body: previewFile,
      signal: abortController.signal
    })

    if (!response.ok) {
      throw new Error(`Failed to simulate print output: ${response.statusText} (${response.status})`)
    }

    const blob = await response.blob()
    const dataUri = URL.createObjectURL(blob)
    const headers = response.headers
    const isMatching = headers.get('X-Simulate-Print-Match-Preview') === 'true'

    $printSimulationDataUri.set(dataUri)
    $isPrintSimulationMatchPreview.set(isMatching)
  } catch (error) {
    if (get(error, 'name') === 'AbortError') {
      // eslint-disable-next-line no-console
      console.warn('Previous print simulation request has been aborted')

      return
    }

    // eslint-disable-next-line no-console
    console.error('Failed to simulate print output:', error)

    $printSimulationError.set(get(error, 'message', 'Unknown error'))

    return
  } finally {
    $isPrintSimulationLoading.set(false)
  }
}

function getPreviewResolution() {
  const layerElement = $virtualLayersEl.get()

  if (!layerElement) return

  const originalWidth = parseFloat(layerElement.getAttribute('width')!)
  const originalHeight = parseFloat(layerElement.getAttribute('height')!)
  const originalAspectRatio = originalWidth / originalHeight
  const reducedWidth = $previewResolutionWidth.get()
  const reducedHeight = reducedWidth / originalAspectRatio

  return {
    width: reducedWidth,
    height: reducedHeight
  }
}

async function renderPngFromSvgElement(svgElement: HTMLElement, fileName: string) {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) return

  const serializedSvgData = new XMLSerializer().serializeToString(svgElement)
  const canvg = await Canvg.from(ctx, serializedSvgData, {
    ignoreMouse: true,
    ignoreAnimation: true
  })

  await canvg.render()

  return new Promise<File>((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) return

      resolve(new File([blob], fileName, { type: 'image/png' }))
    })
  })
}

async function fetchAndNormalizeSvgImage(url: string, defaultWidth?: string, defaultHeight?: string) {
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`Failed to fetch SVG image from URL: ${url}`)
  }

  const svgMarkup = await response.text()

  const parser = new DOMParser()
  const svgDocument = parser.parseFromString(svgMarkup, 'image/svg+xml')
  const svgElement = svgDocument.documentElement
  const width = svgElement.getAttribute('width') ?? defaultWidth
  const height = svgElement.getAttribute('height') ?? defaultHeight
  const viewBox = svgElement.getAttribute('viewBox') ?? `0 0 ${width} ${height}`

  if (!width || !height) {
    throw new Error(`Invalid SVG image from URL: ${url}`)
  }

  svgElement.setAttribute('width', width)
  svgElement.setAttribute('height', height)
  svgElement.setAttribute('viewBox', viewBox)

  return {
    svgElement,
    width,
    height,
    viewBox,
    dataUri: `data:image/svg+xml;base64,${btoa(svgMarkup)}`
  }
}
